/**
 * Prompt Service - Prepares prompts for API calls
 */

import { getBackgroundInfo, getColumnBackgroundInfo } from "../ui/backgroundDialog.js";
import { isEditMode } from "../ui/settingsUI.js";
import { debugLog, debugError } from "../config/debugConfig.js";

const MAX_ROWS = 500;

/**
 * Prepares all prompt data for both API calls and token estimation
 * Returns: { isPartsData, systemPrompt, userPrompt, data, originalValues, metadata }
 */
export async function preparePromptData(sheet, selectedRange, message) {
  // Load basic range properties
  selectedRange.load("rowIndex, rowCount, columnIndex, columnCount, address");
  
  // Check if parts data sheet
  const cellA1 = sheet.getRange("A1");
  cellA1.load("values");
  
  await sheet.context.sync();
  
  const isPartsData = cellA1.values[0][0] === "part_P_ARTICLE_PARTNR";
  
  if (isPartsData) {
    return await preparePartsDataPrompt(sheet, selectedRange, message);
  } else {
    return await prepareGenericDataPrompt(sheet, selectedRange, message);
  }
}

async function preparePartsDataPrompt(sheet, selectedRange, message) {
  const startCol = selectedRange.columnIndex;
  var colCount = selectedRange.columnCount;
  var rowCount = selectedRange.rowCount;
  
  // Handle large selections (whole column/row)
  if (colCount > 100 || rowCount > 100) {
    const usedRange = selectedRange.getUsedRangeOrNullObject();
    usedRange.load("columnCount, rowCount");
    await sheet.context.sync();
    if (!usedRange.isNullObject) {
      colCount = usedRange.columnCount;
      rowCount = usedRange.rowCount;
    }
  }
  
  // If selection starts before row 4, force it to start at row 4
  const startRow = Math.max(selectedRange.rowIndex, 3);
  const selectionEndRow = selectedRange.rowIndex + rowCount - 1;
  rowCount = selectionEndRow - startRow + 1;
  
  if (rowCount <= 0) {
    return null;
  }

  if (rowCount > MAX_ROWS) {
    return { error: `❌ Die Auswahl enthält zu viele Zeilen (${rowCount}). Bitte wähle maximal ${MAX_ROWS} Zeilen aus.` };
  }
  
  // Get headers from row 3
  const headerA = sheet.getRangeByIndexes(2, 0, 1, 1);
  const headerSelection = sheet.getRangeByIndexes(2, startCol, 1, colCount);
  
  // Get data starting from adjusted row
  const columnA = sheet.getRangeByIndexes(startRow, 0, rowCount, 1);
  const selection = sheet.getRangeByIndexes(startRow, startCol, rowCount, colCount);
  
  // Find product group columns AND get column IDs for selected columns
  // Read enough columns to cover the selection
  const maxColumnToRead = Math.max(20, startCol + colCount);
  const columnIdsRow = sheet.getRangeByIndexes(0, 0, 1, maxColumnToRead);
  columnIdsRow.load("values");
  
  headerA.load("values");
  headerSelection.load("values");
  columnA.load("values");
  selection.load("values");
  
  await sheet.context.sync();
  
  // Find column indices for product group columns
  let topGroupColIndex = -1;
  let groupColIndex = -1;
  let subGroupColIndex = -1;
  
  const columnIds = columnIdsRow.values[0];
  for (let i = 0; i < columnIds.length; i++) {
    const columnId = columnIds[i];
    if (columnId === "part_P_ARTICLE_PRODUCTTOPGROUP") {
      topGroupColIndex = i;
    } else if (columnId === "part_P_ARTICLE_PRODUCTGROUP") {
      groupColIndex = i;
    } else if (columnId === "part_P_ARTICLE_PRODUCTSUBGROUP") {
      subGroupColIndex = i;
    }
  }
  
  // Load product group data
  let productGroupsData = {};
  try {
    const response = await fetch('./data/produktgruppen.json');
    productGroupsData = await response.json();
  } catch (error) {
    debugError("Error loading produktgruppen.json:", error);
  }
  
  // Get product group values for each data row
  const productGroupNames = [];
  if (topGroupColIndex >= 0 || groupColIndex >= 0 || subGroupColIndex >= 0) {
    let topGroupRange, groupRange, subGroupRange;
    
    if (topGroupColIndex >= 0) {
      topGroupRange = sheet.getRangeByIndexes(startRow, topGroupColIndex, rowCount, 1);
      topGroupRange.load("values");
    }
    if (groupColIndex >= 0) {
      groupRange = sheet.getRangeByIndexes(startRow, groupColIndex, rowCount, 1);
      groupRange.load("values");
    }
    if (subGroupColIndex >= 0) {
      subGroupRange = sheet.getRangeByIndexes(startRow, subGroupColIndex, rowCount, 1);
      subGroupRange.load("values");
    }
    
    await sheet.context.sync();
    
    for (let i = 0; i < rowCount; i++) {
      let topGroupName = "";
      let groupName = "";
      let subGroupName = "";
      
      if (topGroupRange && topGroupRange.values[i] && topGroupRange.values[i][0] !== null && topGroupRange.values[i][0] !== undefined && topGroupRange.values[i][0] !== "") {
        const topGroupId = topGroupRange.values[i][0].toString();
        topGroupName = productGroupsData.PRODUCTTOPGROUP?.[topGroupId] || `TopGroup ${topGroupId}`;
      }
      
      if (groupRange && groupRange.values[i] && groupRange.values[i][0] !== null && groupRange.values[i][0] !== undefined && groupRange.values[i][0] !== "") {
        const groupId = groupRange.values[i][0].toString();
        groupName = productGroupsData.PRODUCTGROUP?.[groupId] || `Group ${groupId}`;
      }
      
      if (subGroupRange && subGroupRange.values[i] && subGroupRange.values[i][0] !== null && subGroupRange.values[i][0] !== undefined && subGroupRange.values[i][0] !== "") {
        const subGroupId = subGroupRange.values[i][0].toString();
        subGroupName = productGroupsData.PRODUCTSUBGROUP?.[subGroupId] || `SubGroup ${subGroupId}`;
      }
      
      const combinedName = [topGroupName, groupName, subGroupName].filter(n => n).join(" > ");
      productGroupNames.push(combinedName);
    }
  }
  
  // Build 2D array
  const data2D = [
    [headerA.values[0][0], "Produktgruppe", ...headerSelection.values[0]]
  ];
  
  for (let i = 0; i < rowCount; i++) {
    const productGroupName = productGroupNames[i] || "";
    data2D.push([columnA.values[i][0], productGroupName, ...selection.values[i]]);
  }
  
  // Remove trailing empty rows
  const filteredData2D = [data2D[0]];
  let lastDataRowIndex = 0;
  for (let i = 1; i < data2D.length; i++) {
    const row = data2D[i];
    const hasData = row.some(cell => cell !== null && cell !== undefined && cell !== "");
    if (hasData) {
      lastDataRowIndex = i;
    }
  }
  for (let i = 1; i <= lastDataRowIndex; i++) {
    filteredData2D.push(data2D[i]);
  }
  
  const filteredDataRowCount = filteredData2D.length - 1;
  
  // Get background context
  const columnBackgroundInfo = getColumnBackgroundInfo();
  const backgroundInfo = getBackgroundInfo();
  const backgroundTexts = [];
  const colNames = headerSelection.values[0];
  
  for (let i = 0; i < colCount; i++) {
    const colIdx = startCol + i;
    if (colIdx < columnIds.length && columnBackgroundInfo[columnIds[colIdx]]) {
      backgroundTexts.push(`${colNames[i]}: ${columnBackgroundInfo[columnIds[colIdx]]}`);
    }
  }
  
  let backgroundContext = "";
  if (backgroundTexts.length > 0) {
    backgroundContext = `\n\nSpaltenbezogene Hintergrundinformationen:\n${backgroundTexts.join('\n')}`;
  } else if (backgroundInfo && backgroundInfo.trim()) {
    backgroundContext = `\n\nZusätzliche Hintergrundinformationen vom Benutzer:\n${backgroundInfo}`;
  }
  
  const modeNote = isEditMode() ? "" : "\n\nHINWEIS: Daten NICHT bearbeiten, nur analysieren.";
  
  const systemPrompt = `Du bist ein hilfreicher Assistent in Microsoft Excel.\nDu unterstützt bei der Analyse, Bereinigung und Aufbereitung von EPLAN-Artikeldaten.\nDu erhältst immer ein 2D-Array: 1.Spalte = Artikelnummern (um nach fehlenden Informationen zu suchen oder vorhandene Daten im Web zu überprüfen), 2.Spalte = Produktgruppen (als Hintergrundinfo), restliche Spalten = anzupassende Werte. Der Header enthält die Eigenschaftsnamen. Sprachkürzel wie (de_DE) oder (en_US) im Spaltennamen geben die Sprache des Spalteninhalts an. Bei Übersetzungsaufgaben: Den Inhalt der Quellsprachen-Spalte NICHT verändern – die Übersetzung gehört in die Zielspalte mit dem entsprechenden Sprachkürzel.\nAnalysiere die Daten, erkläre kurz deine Änderungen und gib die Daten zurück (im 2D-Format, ohne Spalte 2, aber MIT Kopfzeile und MIT Spalte 1 Artikelnummer als erste Spalte zur Referenz).\n\nAntworte IMMER mit folgendem JSON:\n{"text": "Kurze sachliche Erklärung deiner Änderungen", "data": [[bereinigte 2D-Liste mit ${filteredDataRowCount + 1} Zeilen (1 Kopfzeile + ${filteredDataRowCount} Datenzeilen) und ${colCount + 1} Spalten. Erste Spalte = Artikelnummer (unveränderlich). Erste Zeile = Kopfzeile. Keine Zeilen entfernen. Keine Spalten entfernen. Zeilen- und Spaltenreihenfolge muss gleich bleiben]]}\n${modeNote}${backgroundContext}`;
  
  const userPrompt = `${message}\n\nBereich: ${selectedRange.address}\n\nDaten:\n${JSON.stringify(filteredData2D, null, 2)}`;
  
  return {
    isPartsData: true,
    systemPrompt,
    userPrompt,
    data: filteredData2D,
    originalValues: selection.values,
    metadata: {
      startRow,
      startCol,
      rowCount: filteredDataRowCount,
      colCount
    }
  };
}

async function prepareGenericDataPrompt(sheet, selectedRange, message) {
  var colCount = selectedRange.columnCount;
  var rowCount = selectedRange.rowCount;
  
  // Handle large selections (whole column/row)
  if (colCount > 100 || rowCount > 100) {
    const usedRange = selectedRange.getUsedRangeOrNullObject();
    usedRange.load("columnCount, rowCount");
    await sheet.context.sync();
    if (!usedRange.isNullObject) {
      colCount = usedRange.columnCount;
      rowCount = usedRange.rowCount;
    }
  }
  
  const selection = sheet.getRangeByIndexes(selectedRange.rowIndex, selectedRange.columnIndex, rowCount, colCount);
  selection.load("values, address");
  await sheet.context.sync();
  
  const selectionData = selection.values;
  const filteredRowCount = selectionData.length;

  if (filteredRowCount > MAX_ROWS) {
    return { error: `❌ Die Auswahl enthält zu viele Zeilen (${filteredRowCount}). Bitte wähle maximal ${MAX_ROWS} Zeilen aus.` };
  }
  
  const backgroundInfo = getBackgroundInfo();
  const backgroundContext = (backgroundInfo && backgroundInfo.trim()) 
    ? `\n\nZusätzliche Hintergrundinformationen vom Benutzer:\n${backgroundInfo}` 
    : "";
  const modeNote = isEditMode() ? "" : "\n\nHINWEIS: Daten NICHT bearbeiten, nur analysieren.";
  
  const systemPrompt = `Du bist ein hilfreicher Assistent in Microsoft Excel.\nDu unterstützt bei der Analyse, Bereinigung und Aufbereitung von ECAD-Daten (wie EPLAN).\nDu erhältst Daten und eine Benutzeranfrage.\nAnalysiere die Daten gemäß der Anfrage und gib das Ergebnis zurück.\n\nAntworte IMMER mit folgendem JSON:\n{"text": "Erklärung oder Antwort auf die Anfrage", "data": [[angepasste 2D-Datenliste mit ${filteredRowCount} Zeilen und ${colCount} Spalten]] oder null}\n\n- Bei reiner Analyse oder Erklärung: "data" = null.\n- Bei Anpassungen: Array muss exakt ${filteredRowCount} Zeilen und ${colCount} Spalten haben.${modeNote}${backgroundContext}`;
  
  const userPrompt = `${message}\n\nBereich: ${selection.address}\n\nDaten:\n${JSON.stringify(selectionData, null, 2)}`;
  
  return {
    isPartsData: false,
    systemPrompt,
    userPrompt,
    data: selectionData,
    originalValues: selectionData,
    metadata: {
      startRow: selectedRange.rowIndex,
      startCol: selectedRange.columnIndex,
      rowCount: filteredRowCount,
      colCount
    }
  };
}

export function createResponseFormat(isPartsData, rowCount, colCount) {
  if (isPartsData) {
    return {
      type: "json_schema",
      json_schema: {
        name: "data_analysis_response",
        description: "Structured response for Excel data analysis with exact dimensions.",
        schema: {
          type: "object",
          properties: {
            text: { type: "string", description: "Brief explanation of changes made to the data" },
            data: {
              type: "array",
              description: `2D array with exactly ${rowCount + 1} rows (1 header row + ${rowCount} data rows) and ${colCount + 1} columns: first column is Artikelnummer (unchanged reference), remaining ${colCount} columns are the modified values`,
              items: { type: "array", items: { type: "string" }, minItems: colCount + 1, maxItems: colCount + 1 },
              minItems: rowCount + 1,
              maxItems: rowCount + 1
            }
          },
          required: ["text", "data"],
          additionalProperties: false
        },
        strict: true
      }
    };
  } else {
    return {
      type: "json_schema",
      json_schema: {
        name: "generic_data_response",
        description: "Structured response for Excel data analysis with exact dimensions",
        schema: {
          type: "object",
          properties: {
            text: { type: "string", description: "Explanation or response to the query" },
            data: {
              type: "array",
              description: `2D array with exactly ${rowCount} rows and ${colCount} columns when data modifications are made. Return empty array [] when only analysis.`,
              items: { type: "array", items: { type: "string" }, minItems: colCount, maxItems: colCount }
            }
          },
          required: ["text", "data"],
          additionalProperties: false
        },
        strict: true
      }
    };
  }
}